<svelte:options accessors={true}/>

<script>
  import { onDestroy, tick } from 'svelte';
  import Icon from '../Icon';
  import { useCheckboxGroup, INDETERMINATE } from '../CheckboxGroup';

  const group = useCheckboxGroup();

  export let name = undefined;
  export let value = undefined;
  export let indeterminate = group ? $group.get(value) === INDETERMINATE : false;
  export let checked = group ? $group.get(value) === true : false;
  export let disabled = false;
  export let testid = undefined;
  export let tooltip = undefined;
  export let exclusive = false;
  export let list = () => {};

  let input;
  
  export async function focus() {
    await tick();
    input.focus();
  }
  export function getPrimaryElement() {
    return input;
  }

  $: applyTooltip = tooltip ?? (() => {});

  function keyHandler(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      input.click();
    }
  }

  if (group) {
    // some reason this does not work as $: checked = $group.includes(value);
    onDestroy(group.subscribe((g) => {
      if (g.get(value) && exclusive && g.length > 1) {
        group.update((groupMap) => {
          groupMap.delete(value);
          return groupMap;
        });
      }
      checked = g.get(value) === true;
      indeterminate = g.get(value) === INDETERMINATE;
    }));
  }

  $: {
    if (group) {
      if (checked || indeterminate) {
        if (exclusive) {
          group.set(new Map([[value, true]]));
        } else if (!indeterminate) {
          group.update((g) => {
            g.set(value, true);
            return g;
          });
        }
      } else {
        group.update((g) => {
          g.delete(value);
          return g;
        });
      }
    }
  }
</script>

<div class='checkbox-container'>
  <label class='checkbox' class:disabled data-cy={testid}>
    <input
      type='checkbox'
      {name}
      bind:this={input}
      bind:checked
      bind:indeterminate
      on:change
      {disabled}
      tabindex='-1'
      />
    <span class='checkmark'
      class:disabled
      role='checkbox'
      aria-checked={indeterminate ? 'mixed' : checked}
      use:applyTooltip={{ label: true, focus: true }}
      use:list={!disabled}
      tabindex={disabled ? '-1' : '0'}
      on:keydown={keyHandler}>
      <span class='icon' class:checked={indeterminate}>
        <Icon icon='remove' />
      </span>
      <span class='icon' class:checked>
        <Icon icon='check' />
      </span>
    </span>
    <slot />
  </label>
  {#if tooltip}
    <div class='tooltip-anchor' use:applyTooltip={{ anchor: true, mouse: true }}>
      <Icon icon='help' size={20} />
    </div>
  {/if}
</div>

<style lang="postcss">
  .checkbox-container {
    display: flex;
    gap: var(--spacing--xs);
  }

  .checkbox {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing--xs);
    cursor: pointer;
    user-select: none;
    font: var(--Checkbox--font, var(--font--input));

    & input {
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;
      top: 0;
      left: 0;
    }

    &.disabled {
      pointer-events: none;
      color: var(--color--disabled--text);
    }

    &:focus-within {
      outline: var(--border--divider);
    }
  }

  .tooltip-anchor {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    transition: color var(--duration--extra-fast) var(--ease--on-screen);

    --Icon--color: var(--color--help);
  }

  .tooltip-anchor:hover {
    --Icon--color: var(--color--help-hover);
  }

  .checkmark {
    position: relative;
    box-sizing: border-box;
    height: var(--Checkbox--size, var(--size--icon));
    width: var(--Checkbox--size, var(--size--icon));
    flex-shrink: 0;
    color: var(--Checkbox--color, var(--color--white));
    background-color: var(--Checkbox--background-color, var(--color--white));
    border: var(--size--border) solid var(--Checkbox--border-color, var(--color--grey-20));
    border-radius: 6px;
    transition:
      background-color var(--duration--extra-fast) var(--ease--on-screen),
      border-color var(--duration--extra-fast) var(--ease--on-screen);
    
    &.disabled {
      background-color: var(--Checkbox--background-color, var(--color--grey-5));
    }

    --Icon--color: var(--Checkbox--color, var(--color--white));
  }

  .icon {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity var(--duration--extra-fast) var(--ease--on-screen);

    &.checked {
      opacity: 1;
    }
  }

  .checkbox:hover input:enabled + .checkmark {
    background-color: var(--Checkbox--background-color, var(--color--grey-10));
  }

  .checkbox:active input:enabled + .checkmark {
    background-color: var(--Checkbox--background-color, var(--color--placeholder));
    border-color: var(--Checkbox--border-color, var(--color--placeholder));
  }

  .checkbox input:enabled:checked + .checkmark,
  .checkbox input:enabled:indeterminate + .checkmark {
    background-color: var(--Checkbox--background-color, var(--color--active));
    border-color: var(--Checkbox--border-color, var(--color--active));
  }

  .checkbox input:disabled:checked + .checkmark,
  .checkbox input:disabled:indeterminate + .checkmark {
    color: var(--Checkbox--color, var(--color--disabled--text));
    background-color: var(--Checkbox--background-color, var(--color--secondary--background));
    border-color: var(--Checkbox--border-color, var(--color--secondary--background));
    --Icon--color: var(--color--disabled--text);
  }

  .checkbox:hover input:enabled:checked + .checkmark,
  .checkbox:hover input:enabled:indeterminate + .checkmark {
    background-color: var(--Checkbox--background-color, var(--color--primary--hover));
    border-color: var(--Checkbox--border-color, var(--color--primary--hover));
  }

  .checkbox:active input:enabled:checked + .checkmark,
  .checkbox:active input:enabled:indeterminate + .checkmark {
    background-color: var(--Checkbox--background-color, var(--color--primary--active));
    border-color: var(--Checkbox--border-color, var(--color--primary--active));
  }
</style>
